<template>
  <div class="flex size-full flex-col text-gray-700">
    <p class="text-left text-xl md:text-2xl">
      <span class="text-xl md:text-2xl">Con esto terminamos,</span>
      <span class="font-serif text-xl md:text-2xl"> ¿dónde está tu tienda?</span>
    </p>
    <form
      class="flex w-full flex-col"
      @submit="onSubmit"
    >
      <div
        v-if="!regionsQuery.isLoading.value"
        class="mt-5 flex w-full flex-col md:mt-8"
      >
        <p class="mt-1 text-sm md:text-base">Región de tu tienda</p>
        <lokal-generic-combobox
          v-model="selectedRegion"
          :options="regions"
          :get-option-key="(option: Region) => option.id"
          :get-option-label="(option: Region) => option.name"
          option-label="name"
          options-label="Región de destino"
          text-size="text-xs md:text-sm"
          :multiple="false"
        />
        <Field
          v-model="selectedRegionId"
          hidden
          name="selectedRegionId"
          label="Región de destino"
          :rules="{ required: true }"
        />
        <ErrorMessage
          name="selectedRegionId"
          class="text-sm text-red-500"
        />
      </div>
      <div
        v-if="!communesQuery.isLoading.value"
        class="mt-5 flex w-full flex-col md:mt-8"
      >
        <p class="mt-1 text-sm md:text-base">Comuna de tu tienda</p>
        <lokal-generic-combobox
          v-model="selectedCommune"
          :options="filterCommunes"
          :get-option-key="(option: Commune) => option.id"
          :get-option-label="(option: Commune) => option.name"
          option-label="name"
          options-label="Comuna de destino"
          text-size="text-xs md:text-sm"
        />
        <Field
          v-model="selectedCommuneId"
          hidden
          name="selectedCommuneId"
          label="Comuna de destino"
          :rules="{ required: true }"
        />
        <ErrorMessage
          name="selectedCommuneId"
          class="text-sm text-red-500"
        />
      </div>
      <div class="mt-5 flex w-full flex-col items-center space-x-3 md:mt-8"></div>
      <div
        v-if="signUpStore.shopForm.registrationDiscountCodeToken"
        class="mt-1 flex flex-col"
      >
        <p class="mb-1 text-xs md:text-sm">Código de descuento para tu primera compra:</p>
        <div class="mr-auto rounded-full bg-lk-light-purple p-1 text-xs text-white md:text-sm">
          {{ signUpStore.shopForm.registrationDiscountCodeToken }}
        </div>
      </div>
      <lokal-loading
        v-if="isLoading"
        class="mt-3 self-center"
        :loading="isLoading"
      />
      <div
        v-else
        class="my-3 flex w-full flex-col items-center"
      >
        <button class="mb-1 w-full rounded-md bg-gray-700 p-2 text-sm text-white hover:bg-gray-800 md:text-base">
          Guardar y finalizar
        </button>
        <a
          class="mt-1 cursor-pointer text-sm hover:underline"
          @click="previousStep"
        >
          Volver
        </a>
        <div class="flex w-full items-center justify-between text-xs md:text-sm">
          <button
            class="underline hover:font-semibold hover:text-gray-800"
            @click="resetStoresAndSignOut"
          >
            No soy una tienda
          </button>
          <a
            class="underline hover:font-semibold hover:text-gray-800"
            :href="`https://wa.me/${phoneNumber}?text=${whatsappText}`"
            target="_blank"
            noopener
          >
            Necesito ayuda
          </a>
        </div>
      </div>
    </form>
    <address-new-dialog
      v-model="showAddressNewDialog"
      :addressable-id="1"
      addressable-type="Shop"
    />
  </div>
</template>
<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import shopsApi from 'api/shops';
import { useMutation } from 'vue-query';
import useSignUpStore from 'stores/sign-up-store';
import useSessionStore from 'stores/user-store';
import { useForm, ErrorMessage, Field } from 'vee-validate';
import AddressNewDialog from './address-new-dialog.vue';
import lokalGenericCombobox from './shared/lokal-generic-combobox.vue';
import useRegionsQuery from './queries/regions-query';
import useCommunesQuery from './queries/communes-query';
import useShopQuery from './queries/shop-query';

function previousStep() {
  signUpStore.currentStep = 'shop-sales';
}

const sessionStore = useSessionStore();
const signUpStore = useSignUpStore();

const showAddressNewDialog = ref(false);

const { shop, shopQuery } = useShopQuery(computed(() => sessionStore.user?.shopId));
const { regions, regionsQuery } = useRegionsQuery();
const { communes, communesQuery } = useCommunesQuery();
const selectedRegion = ref({} as Region);
const selectedRegionId = computed(() => selectedRegion.value.id as number);
const selectedCommune = ref({} as Commune);
const selectedCommuneId = computed(() => selectedCommune.value.id as number);
const filterCommunes = computed(() =>
  communes.value?.filter((commune) => commune.regionId === selectedRegion.value.id),
);
const shopCreationData = computed(() => ({
  ...signUpStore.shopForm,
  referenceCommuneId: selectedCommuneId.value,
  referenceRegionId: selectedRegionId.value,
}));

watch(selectedRegion, () => {
  selectedCommune.value = {} as Commune;
});

async function shopCreationOrUpdateSuccess() {
  signUpStore.resetShopForm();
  await sessionStore.reloadUser();
  signUpStore.currentStep = 'success';
}

const { handleSubmit } = useForm();
const createShopMutation = useMutation(() => shopsApi.create(shopCreationData.value), {
  onSuccess: () => {
    shopCreationOrUpdateSuccess();
  },
});
const updateShopMutation = useMutation(() => shopsApi.update(shop.value.id, shopCreationData.value), {
  onSuccess: () => {
    shopCreationOrUpdateSuccess();
  },
});
const isLoading = computed(
  () =>
    createShopMutation.isLoading.value ||
    communesQuery.isLoading.value ||
    (regionsQuery.isLoading.value && shopQuery.isLoading.value),
);

const onSubmit = handleSubmit(() => {
  if (shop.value?.id) {
    updateShopMutation.mutate();
  } else {
    createShopMutation.mutate();
  }
});

watch(shop, (newValue, oldValue) => {
  if (!oldValue?.id && newValue.id) {
    signUpStore.shopForm.name = newValue.name;
    signUpStore.shopForm.webpageLink = newValue.webpageLink as string;
    signUpStore.shopForm.kind = newValue.kind;
    signUpStore.shopForm.otherKind = newValue.otherKind;
  }
});

function resetStoresAndSignOut() {
  sessionStore.reset();
  signUpStore.reset();
  window.location.href = '/users/sign_out';
}

const phoneNumber = computed(() => process.env.CONTACT_NUMBER);
const whatsappText = computed(() => 'Necesito ayuda en la creación de mi tienda.');
</script>
